import * as Sentry from "@sentry/react";
import { createContext, useContext } from "react";

import { SqlRbacAction } from "services/rbac/action";
import { Privilege, PrivilegeObjectType } from "services/rbac/types";

// Reserved object name (uses invalid characters not to mix with real object names)
export const ANY_OBJECT_NAME = "_$ANY OBJECT NAME$_";

type ActionMap = Partial<Record<SqlRbacAction, SqlRbacAction>>;

const engineRelatedActionMap: ActionMap = {
  [SqlRbacAction.MODIFY]: SqlRbacAction.ENGINE_MODIFY_ANY,
  [SqlRbacAction.USAGE]: SqlRbacAction.ENGINE_USAGE_ANY,
  [SqlRbacAction.OPERATE]: SqlRbacAction.ENGINE_OPERATE_ANY,
};

const databaseRelatedActionMap: ActionMap = {
  [SqlRbacAction.MODIFY]: SqlRbacAction.DATABASE_MODIFY_ANY,
  [SqlRbacAction.USAGE]: SqlRbacAction.DATABASE_USAGE_ANY,
};

const roleRelatedActionMap: ActionMap = {
  [SqlRbacAction.MODIFY]: SqlRbacAction.ROLE_MODIFY_ANY,
};

const userRelatedActionMap: ActionMap = {
  [SqlRbacAction.MODIFY]: SqlRbacAction.USER_MODIFY_ANY,
};

/**
 * Related actions allow checking account level permissions for specific object types.
 * E.g., when we call isAllowedTo with objectType "engine" and action "MODIFY",
 * we automatically check if the user has account level permission "ENGINE_MODIFY_ANY".
 */
const ACCOUNT_WIDE_ACTION_MAP: Partial<Record<PrivilegeObjectType, ActionMap>> =
  {
    [PrivilegeObjectType.engine]: engineRelatedActionMap,
    [PrivilegeObjectType.database]: databaseRelatedActionMap,
    [PrivilegeObjectType.role]: roleRelatedActionMap,
    [PrivilegeObjectType.user]: userRelatedActionMap,
  };

const loginRelatedActionMap: ActionMap = {
  [SqlRbacAction.MODIFY]: SqlRbacAction.MODIFY_ANY_LOGIN,
};

const serviceAccountRelatedActionMap: ActionMap = {
  [SqlRbacAction.MODIFY]: SqlRbacAction.MODIFY_ANY_SERVICE_ACCOUNT,
};

const accountRelatedActionMap: ActionMap = {
  [SqlRbacAction.MODIFY]: SqlRbacAction.MODIFY_ANY_ACCOUNT,
};

const networkPolicyRelatedActionMap: ActionMap = {
  [SqlRbacAction.MODIFY]: SqlRbacAction.MODIFY_ANY_NETWORK_POLICY,
};

const organizationRoleRelatedActionMap: ActionMap = {
  [SqlRbacAction.MODIFY]: SqlRbacAction.MODIFY_ANY_ORGANIZATION_ROLE,
};

/**
 * Organization wide actions are used to check if the current login has permission to perform a specific action on an organization level resource.
 * E.g., when we call isAllowedTo with objectType "network_policy" and action "MODIFY",
 * we automatically check if the login has organization level permission "MODIFY_ANY_NETWORK_POLICY".
 */
const ORGANIZATION_WIDE_ACTION_MAP: Partial<
  Record<PrivilegeObjectType, ActionMap>
> = {
  [PrivilegeObjectType.login]: loginRelatedActionMap,
  [PrivilegeObjectType.service_account]: serviceAccountRelatedActionMap,
  [PrivilegeObjectType.account]: accountRelatedActionMap,
  [PrivilegeObjectType.network_policy]: networkPolicyRelatedActionMap,
  [PrivilegeObjectType.organization_role]: organizationRoleRelatedActionMap,
};

const OrganizationPrivilegeObjectTypes = [
  PrivilegeObjectType.organization,
  PrivilegeObjectType.login,
  PrivilegeObjectType.service_account,
  PrivilegeObjectType.account,
  PrivilegeObjectType.network_policy,
  PrivilegeObjectType.organization_role,
];

const AccountPrivilegeObjectTypes = [
  PrivilegeObjectType.role,
  PrivilegeObjectType.engine,
  PrivilegeObjectType.database,
  PrivilegeObjectType.location,
  PrivilegeObjectType.user,
  PrivilegeObjectType.table,
  PrivilegeObjectType.view,
];

type Options = {
  owner?: string | null;
};

type PrivilegeMap = {
  [K in PrivilegeObjectType]?: {
    [objectName: string]: SqlRbacAction[];
  };
};

type AccessManagerUser = {
  userName: string;
  loginName: string | null;
};

export class AccessManager {
  public privileges: PrivilegeMap = {};

  private currentAccountName: string = "";

  private currentOrganizationName: string = "";

  private user: AccessManagerUser = {
    userName: "",
    loginName: null,
  };

  /**
   * Checks if the current user has permission to perform a specific action on a resource.
   *
   * @param objectType - The type of object to check permissions for (e.g., database, engine, role, user)
   * @param objectName - The name of the specific object to check permissions for.
   *   Special cases:
   *   - `ANY_OBJECT_NAME`: Checks if the user has the specified permission on at least one object of the given type.
   *     Use this when you need to verify if a user has a specific privilege on any resource of the provided type.
   *       Example: Checking if a user has MODIFY permission on any network policy by looking through all network policy privileges.
   * @param action - The specific action to check permission for (e.g., MODIFY, USAGE, OPERATE)
   * @param options - Additional options for permission checking
   * @param options.owner - If specified, checks if the current user is the owner of the resource. This is relevant only for account level objects.
   * @returns boolean - True if the user has permission, false otherwise
   */
  isAllowedTo = (
    objectType: PrivilegeObjectType,
    objectName: string,
    action: SqlRbacAction,
    options?: Options
  ): boolean => {
    if (!objectName || objectName === "*") {
      const err = new Error(
        `isAllowedTo called with invalid objectName: ${objectName}`
      );

      Sentry.captureException(err, {
        extra: {
          objectType,
          objectName,
          action,
          options,
        },
      });

      // Keep existing console warning for local debugging
      console.warn(err);
    }

    // Check if user is owner of the resource
    // This is relevant only for account-level objects. Organization-level ownership is handled by the backend.
    if (options?.owner) {
      if (
        AccountPrivilegeObjectTypes.includes(objectType) &&
        this.user.userName === options.owner
      )
        return true;
      if (
        OrganizationPrivilegeObjectTypes.includes(objectType) &&
        this.user.loginName === options.owner
      )
        return true;
    }

    if (objectName === ANY_OBJECT_NAME) {
      const objectTypePrivileges = this.privileges[objectType];

      if (objectTypePrivileges) {
        // Check all objects of this type for the action
        return Object.values(objectTypePrivileges).some(privileges =>
          privileges.includes(action)
        );
      }
      return false;
    }

    const resourcePrivileges = this.privileges[objectType]?.[objectName] ?? [];

    // check exact action on the resource
    if (resourcePrivileges.includes(action)) {
      return true;
    }

    const accountWideAction = ACCOUNT_WIDE_ACTION_MAP[objectType]?.[action];

    const organizationWideAction =
      ORGANIZATION_WIDE_ACTION_MAP[objectType]?.[action];

    if (accountWideAction) {
      const currentAccountPrivileges =
        this.privileges[PrivilegeObjectType.account]?.[
          this.currentAccountName
        ] ?? [];

      if (currentAccountPrivileges.includes(accountWideAction)) {
        return true;
      }
    }

    if (organizationWideAction) {
      const currentOrganizationPrivileges =
        this.privileges[PrivilegeObjectType.organization]?.[
          this.currentOrganizationName
        ] ?? [];

      if (currentOrganizationPrivileges.includes(organizationWideAction)) {
        return true;
      }
    }

    return false;
  };

  initAccessManager = async (
    privileges: Privilege[],
    currentAccountName: string,
    currentOrganizationName: string,
    user: AccessManagerUser
  ): Promise<void> => {
    const byResource: PrivilegeMap = {};

    for (const privilege of privileges) {
      const { objectType, objectName, privilegeType } = privilege;

      if (objectName === undefined) {
        continue; // eslint-disable-line no-continue -- Skip undefined object names, they are not valid while initializing
      }

      if (!byResource[objectType]) {
        byResource[objectType] = {};
      }

      if (!byResource[objectType]![objectName]) {
        byResource[objectType]![objectName] = [];
      }

      byResource[objectType]![objectName].push(privilegeType);
    }

    this.privileges = byResource;
    this.currentAccountName = currentAccountName;
    this.currentOrganizationName = currentOrganizationName;
    this.user = user;
  };

  onObjectNameChange = ({
    currentObjectName,
    newObjectName,
    objectType,
  }: {
    currentObjectName: string;
    newObjectName: string;
    objectType: PrivilegeObjectType;
  }) => {
    const resourcePrivileges =
      this.privileges[objectType]?.[currentObjectName] ?? [];
    if (resourcePrivileges.length) {
      if (
        this.privileges[objectType] &&
        !this.privileges[objectType]?.[newObjectName]
      ) {
        this.privileges[objectType][newObjectName] = resourcePrivileges;
      }
    }
  };
}

export const accessManager = new AccessManager();

export const AccessManagerContext = createContext(accessManager);

export const useAccessManager = () => {
  const accessManager = useContext(AccessManagerContext);
  return accessManager;
};
